import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./solar-controls.module.scss";
import { Switch } from "../../../components/switch";
import {
	SolarPowerToggleIcon,
	SolarCO2EmissionIcon,
	SolarEstimatedBillIcon,
} from "../../../../../assets/icons";
import {
	usePauseSolarSubscriptionMutation,
	useResumeSolarSubscriptionMutation,
	useGetSolarConsumptionTotalQuery,
} from "../../../../../service/vending-service";
import { getLastNDays } from "../../../components/date-range/date-range.utils";
import { ApplicationRoutes } from "../../../../../routes";

interface SolarControlsProps {
	subscriptionId: string;
	isReady: boolean;
}

export const SolarControls: React.FC<SolarControlsProps> = ({
	subscriptionId,
	isReady,
}) => {
	const navigate = useNavigate();
	const [isSolarOn, setIsSolarOn] = useState(true);
	const [pauseSolar] = usePauseSolarSubscriptionMutation();
	const [resumeSolar] = useResumeSolarSubscriptionMutation();

	const todayRange = useMemo(() => getLastNDays(1), []);
	const apiParams = useMemo(
		() => ({
			From: todayRange.from.toISOString(),
			To: todayRange.to.toISOString(),
			PayAsYouGoSubscriptionId: subscriptionId,
		}),
		[todayRange, subscriptionId]
	);

	const { data: consumptionData } = useGetSolarConsumptionTotalQuery(
		apiParams,
		{ skip: !isReady }
	);

	const co2Saved = useMemo(() => {
		const generated = consumptionData?.generated ?? 0;
		return Math.round(generated * 0.5);
	}, [consumptionData]);

	const handleToggle = async (checked: boolean) => {
		setIsSolarOn(checked);
		try {
			if (!checked) {
				await pauseSolar({ subscriptionId }).unwrap();
			} else {
				await resumeSolar({ subscriptionId }).unwrap();
			}
		} catch {
			setIsSolarOn(!checked);
		}
	};

	return (
		<div className={styles.container}>
			<div className={`${styles.card} ${styles.toggleCard}`}>
				<div className={styles.cardLeft}>
					<div className={styles.iconCircle}>
						<SolarPowerToggleIcon />
					</div>
					<span className={styles.cardText}>Turn off Solar</span>
				</div>
				<Switch
					checked={!isSolarOn}
					onChange={(checked) => handleToggle(!checked)}
					disabled={!isReady}
				/>
			</div>

			<div className={styles.card}>
				<div className={styles.cardLeft}>
					<div className={styles.co2Icon}>
						<SolarCO2EmissionIcon />
					</div>
					<span className={styles.cardText}>
						{co2Saved}kg CO<sub>2</sub> Emission Saved
					</span>
				</div>
			</div>

			<div
				className={`${styles.card} ${styles.clickableCard}`}
				onClick={() => navigate(`/${ApplicationRoutes.CUSTOMER_ESTIMATED_BILL}`)}
			>
				<div className={styles.cardLeft}>
					<div className={styles.iconCircle}>
						<SolarEstimatedBillIcon />
					</div>
					<span className={styles.cardText}>Estimated Bill</span>
				</div>
			</div>
		</div>
	);
};
